// src/components/dashboard/SparklineChart.tsx
import { SourceHealth } from '@/domain/Health/HealthTypes';
import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getSourceColorByIndex } from '../utils';

interface HighchartsSparklineProps {
	sources: SourceHealth[];
	unit: string | undefined;
}

export const HighchartsSparkline = ({ sources, unit }: HighchartsSparklineProps) => {
	if (!sources || sources.length === 0 || sources.every((s) => s.readings.length === 0)) {
		return <span className="text-gray-400 text-sm">No data</span>;
	}

	// Assume all sources share the same time categories for simplicity

	const series = sources.map((source, index) => {
		const color = getSourceColorByIndex(index);
		return {
			name: source.sourceName,
			id: source.sourceId.toString(),
			data: source.readings.map((r) => [r.time, r.value]),
			showInLegend: false,
			virtual: source.virtual,
			color,
		};
	});

	const options = {
		chart: {
			type: 'line',
			width: 120,
			height: 40,
			backgroundColor: 'transparent',
			margin: [0, 0, 0, 0],
			spacing: [0, 0, 0, 0],
		},
		title: { text: '' },
		legend: { enabled: false },
		xAxis: {
			type: 'datetime',
			visible: false,
			title: {
				text: null,
			},
			startOnTick: false,
			endOnTick: false,
		},
		yAxis: {
			min: 0,
			minorTickInterval: 0.1,
			visible: false,
		},
		plotOptions: {
			line: {
				marker: { enabled: false },
				lineWidth: 2,
			},
		},
		series,
		credits: { enabled: false },
		exporting: { enabled: false },
		tooltip: {
			useHTML: true,
			shared: true,
			outside: true,
			formatter: function () {
				const vm: any = this;
				const header = `<div style="font-weight: 600; text-align: right; font-size: 12px; margin-bottom: 4px;">${dayjs.unix(Math.floor(vm.x / 1000)).format('hh:mm A')}</div>`;
				const items = vm.points
					?.map((p) => {
						return `
              <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px; margin: 2px 0; width: auto; column-gap: 10px">
                <div style="display: flex; align-items: center; gap: 4px;">
                  <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color}"></span>
                  <span>${`${p.series.name} ${p.series.userOptions.virtual ? '(virtual)' : '(physical)'}`}  </span>
                </div>
                <span>${p.y.toFixed(1)} ${unit}</span>
              </div>
            `;
					})
					.join('');

				return `
				${header}
          <div style="padding: 6px 8px; background: white; border-radius: 6px; border:solid 1px #E6E6E6; ">
            ${items}
          </div>
        `;
			},
		},
	};

	return <HighchartsReact highcharts={Highcharts} options={options} />;
};
